import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Link as MuiLink,
  Select,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { Template } from '../../types/template';
import TemplateSelector from './TemplateSelector';

import type { QuickSettings as QuickSettingsType } from './types';

interface QuickSettingsProps {
  quickSettings: QuickSettingsType;
  onQuickSettingsChange: (newSettings: Partial<QuickSettingsType>) => void;
  selectedTemplates: Record<string, string>;
  onTemplateChange: (type: string, templateId: string) => void;
  availableTemplates: Template[];
  clientLanguage?: string | undefined;
  onClientLanguageChange?: (language: string) => void;
  onSaveAsDefault?: () => Promise<void>;
  isSavingAsDefault?: boolean;
  hasDraftGenerated?: boolean;
}

const QuickSettings: React.FC<QuickSettingsProps> = ({
  quickSettings,
  onQuickSettingsChange,
  selectedTemplates,
  onTemplateChange,
  availableTemplates,
  clientLanguage,
  onClientLanguageChange,
  onSaveAsDefault,
  isSavingAsDefault,
  hasDraftGenerated = false,
}) => {
  const theme = useTheme();

  const defaultLanguage = import.meta.env.VITE_PROJECT_ID?.includes('uk-')
    ? 'UK English'
    : import.meta.env.VITE_PROJECT_ID?.includes('us-')
      ? 'US English'
      : 'Australian English';

  const sliderStyles = useMemo(
    () => ({
      '& .MuiSlider-thumb': {
        '&:hover, &.Mui-focusVisible': {
          boxShadow: `0px 0px 0px 8px ${theme.palette.bidlogiq.brick}33`,
        },
        backgroundColor: theme.palette.bidlogiq.brick,
      },
      '& .MuiSlider-track': {
        backgroundColor: theme.palette.bidlogiq.brick,
      },
      '& .MuiSlider-rail': {
        backgroundColor: `${theme.palette.bidlogiq.brick}80`,
      },
      '& .MuiSlider-valueLabel': {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.bidlogiq.brick}`,
      },
      '&.Mui-disabled': {
        '& .MuiSlider-thumb': {
          backgroundColor: theme.palette.secondary.contrastText,
        },
        '& .MuiSlider-track': {
          backgroundColor: theme.palette.secondary.contrastText,
        },
        '& .MuiSlider-rail': {
          backgroundColor: `${theme.palette.secondary.contrastText}80`,
        },
      },
    }),
    [theme]
  );

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography
            variant="body1"
            color="text.primary"
            sx={{ fontSize: '1rem', fontWeight: 'bold' }}
          >
            Usage:
          </Typography>
          <Box
            component="ol"
            sx={{ mt: 1, mb: 0, pl: 2, color: 'text.secondary', fontSize: '0.9rem' }}
          >
            <li>Adjust settings to tailor the generated response.</li>
            <li>Changes are saved automatically.</li>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          {onClientLanguageChange && (
            <FormControl fullWidth>
              <InputLabel id="client-language-select-label">AI Language</InputLabel>
              <Select
                labelId="client-language-select-label"
                id="client-language-select"
                value={clientLanguage || defaultLanguage}
                label="AI Language"
                onChange={(e) => onClientLanguageChange(e.target.value)}
              >
                <MenuItem value="Australian English">Australian English</MenuItem>
                <MenuItem value="UK English">UK English</MenuItem>
                <MenuItem value="US English">US English</MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Controls container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Grid container spacing={4}>
            {/* Row 1: AI Model & Word Count/Confidence */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography>AI Model</Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Gemini 4:</strong> Default model. Experimental, advanced reasoning
                        and outputs.
                      </Typography>
                      <Typography variant="body2">
                        <strong>Gemini 3.1:</strong> Good for standard use cases.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    tabIndex={0}
                    sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
              <FormControl fullWidth>
                <Select
                  id="defaultModelName"
                  SelectDisplayProps={{ id: 'mui-component-select-defaultModelName' }}
                  value={quickSettings.defaultModelName ?? 'gemini-4-argon'}
                  onChange={(e) =>
                    onQuickSettingsChange({ defaultModelName: e.target.value as string })
                  }
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <MenuItem value="gemini-4-argon">Gemini 4 Argon (Experimental)</MenuItem>
                  <MenuItem value="gemini-3.1-pro">Gemini 3.1 Pro (Standard)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography>
                  Narrative Word Limit: <strong>{quickSettings.draftWordLimit ?? 10000}</strong> (
                  {Math.round((quickSettings.draftWordLimit ?? 10000) / 500)} pages*)
                </Typography>
                <Tooltip
                  title="Word count applies only to the core response generated from the outline. It does not include CVs, Case Studies, Executive Summary, or other attachments"
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    tabIndex={0}
                    sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
              <Slider
                value={quickSettings.draftWordLimit ?? 10000}
                onChange={(_, val) => onQuickSettingsChange({ draftWordLimit: val as number })}
                step={500}
                min={1000}
                max={20000}
                valueLabelDisplay="auto"
                sx={sliderStyles}
              />
            </Grid>

            {/* Row 4: Template Selection */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TemplateSelector
                selectedTemplates={selectedTemplates}
                onTemplateChange={onTemplateChange}
                availableTemplates={availableTemplates}
                stacked={false}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {hasDraftGenerated ? (
                <Alert severity="warning">
                  A draft has already been generated. If you change the template, you will need to
                  generate the draft again to ensure all sections are filled correctly against the
                  new template.
                </Alert>
              ) : (
                <Alert severity="info">
                  Select a template to structure your generated response. You can view, create, and
                  upload custom templates in{' '}
                  <MuiLink
                    component={RouterLink}
                    to="/templates"
                    sx={{ fontWeight: 'bold', color: 'info.main' }}
                  >
                    Manage Templates
                  </MuiLink>
                  .
                </Alert>
              )}
            </Grid>

            {/* Row 5: Save Defaults */}
            {onSaveAsDefault && (
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveOutlinedIcon />}
                  onClick={onSaveAsDefault}
                  disabled={!!isSavingAsDefault}
                  sx={{
                    bgcolor: theme.palette.bidlogiq.brick,
                    color: 'white',
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: theme.palette.bidlogiq.rust,
                    },
                  }}
                >
                  Save as Default
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default QuickSettings;
